import {
	Avatar,
	Button,
	Card,
	Col,
	Form,
	Image,
	Input,
	List,
	Modal,
	Row,
	Select,
	Space,
	Switch,
	Typography,
	Upload,
	message,
} from 'antd'
import {
	CloseCircleOutlined,
	SearchOutlined,
	UploadOutlined,
	UserOutlined,
} from '@ant-design/icons'
import _ from 'lodash'
import React from 'react'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { Colors } from '../../common/constant'
import { departmentService, roleService, shiftService } from '../../common/service'
import { SEX_OPTIONS } from '../../common/service/CommonService'
import { staffService } from '../../common/service/staff.service'
import { IStaff } from '../../Interfaces/StaffInterface'
import { useNavigate, useParams } from 'react-router-dom'
import SuccessfulModal from '../../common/components/ConfirmModel'
import { useTheme, useThemeColors } from '../../common/ThemeContext'

interface Props {
	staffIdOverride?: string
	embedded?: boolean
	onClose?: () => void
	onSuccess?: () => void
}

const StaffCreateUpdate: React.FC<Props> = ({
	staffIdOverride,
	embedded = false,
	onClose,
	onSuccess,
}) => {
	const INITIAL_STATE: IStaff = {
		firstName: '',
		lastName: '',
		enName: '',
		gender: '',
		department: '',
		shift: '',
		role: '',
		bankAccount: '',
		phoneNumber: '',
		email: '',
		id: '',
		status: 'Active',
		extraShift: '',
		isExtraShift: false,
		photoUrl: '',
		ranking: undefined,
	}
	const navigate = useNavigate()
	const [formState, setFormState] = React.useState<IStaff>(INITIAL_STATE)
	const [photoFile, setPhotoFile] = React.useState<File | null>(null)
	const [openSuccess, setOpenSuccess] = React.useState<boolean>(false)
	const [departmentOption, setDepartmentOption] = React.useState<
		Array<{ value: string; label: string }>
	>([])
	const [shiftOption, setShiftOption] = React.useState<Array<{ value: string; label: string }>>(
		[],
	)
	const [nightShiftOption, setNightShiftOption] = React.useState<
		Array<{ value: string; label: string }>
	>([])
	const [roleOption, setRoleOption] = React.useState<Array<{ value: string; label: string }>>([])
	const { id: routeStaffId } = useParams()
	const staffId = staffIdOverride || routeStaffId
	const [form] = Form.useForm()

	// Manager lookup state
	const [firstLineManager, setFirstLineManager] = React.useState<{
		id: string
		name: string
		photoUrl?: string
	} | null>(null)
	const [secondLineManager, setSecondLineManager] = React.useState<{
		id: string
		name: string
		photoUrl?: string
	} | null>(null)
	const [thirdLineManager, setThirdLineManager] = React.useState<{
		id: string
		name: string
		photoUrl?: string
	} | null>(null)
	const [lookupTarget, setLookupTarget] = React.useState<'first' | 'second' | 'third' | null>(
		null,
	)
	const [lookupSearch, setLookupSearch] = React.useState('')
	const [lookupResults, setLookupResults] = React.useState<
		{ id: string; name: string; nameKh?: string; photoUrl?: string }[]
	>([])
	const [lookupLoading, setLookupLoading] = React.useState(false)
	const [l2Suggestion, setL2Suggestion] = React.useState<{
		id: string
		name: string
		photoUrl?: string
		sourceLabel?: string
	} | null>(null)
	const [l2SuggestionDismissed, setL2SuggestionDismissed] = React.useState(false)

	//   Constant
	const genderOption = SEX_OPTIONS
	const statusOption = [
		{
			value: 'Active',
			label: 'Active',
		},
		{
			value: 'InActive',
			label: 'Inactive',
		},
	]
	const rankingOption = [
		{ value: 'executor', label: 'Executor' },
		{ value: 'manager_level_1', label: 'Manager Level 1' },
		{ value: 'manager_level_2', label: 'Manager Level 2' },
		{ value: 'manager_level_3', label: 'Manager Level 3' },
	]

	// console.log('formState', formState)

	//Function Handler
	const handlePhotoUpload = (info: any) => {
		const file = info.file
		if (file.status === 'uploading') {
			return
		}
		// Validate file type
		const isJpgOrPng =
			file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp'
		if (!isJpgOrPng) {
			message.error('You can only upload JPG/PNG/WebP files!')
			return
		}
		// Validate file size (5MB)
		const isLt5M = file.size / 1024 / 1024 < 5
		if (!isLt5M) {
			message.error('Image must be smaller than 5MB!')
			return
		}

		// Store file for upload and create preview URL
		setPhotoFile(file.originFileObj || file)
		const reader = new FileReader()
		reader.onload = (e: any) => {
			setFormState((prev) => ({
				...prev,
				photoUrl: e.target.result,
			}))
		}
		reader.readAsDataURL(file.originFileObj || file)
	}

	const handleChange = (event: any) => {
		setFormState((prev) => ({
			...prev,
			[event.target.name]: event.target.value,
		}))
	}
	const handleChangeSwitch = (checked: boolean) => {
		// console.log('checked', checked)
		setFormState((prev) => ({
			...prev,
			['isExtraShift']: checked,
		}))
	}
	const buildManagerPayload = (id?: string, name?: string, photoUrl?: string) => {
		const trimmedId = (id || '').trim()
		const trimmedName = (name || '').trim()

		if (!trimmedId || !trimmedName) {
			return undefined
		}

		return _.omitBy(
			{
				id: trimmedId,
				name: trimmedName,
				photoUrl: (photoUrl || '').trim() || undefined,
			},
			_.isNil,
		)
	}

	const isValidL2Candidate = React.useCallback(
		(candidate?: { id?: string | null } | null) => {
			const candidateId = (candidate?.id || '').trim()
			if (!candidateId) return false
			const staffIdValue = (formState.id || '').trim()
			const firstId = (firstLineManager?.id || '').trim()
			if (staffIdValue && candidateId === staffIdValue) return false
			if (firstId && candidateId === firstId) return false
			return true
		},
		[formState.id, firstLineManager?.id],
	)

	const refreshL2Suggestion = React.useCallback(
		async (firstMgrId?: string | null) => {
			const id = (firstMgrId || '').trim()
			if (!id) {
				setL2Suggestion(null)
				return
			}
			try {
				const res: any = await staffService.detail(id)
				const d = res?.data || {}
				const candidatePrimary = d?.firstLineManager?.id
					? {
							id: d.firstLineManager.id as string,
							name: d.firstLineManager.name as string,
							photoUrl: d.firstLineManager.photoUrl as string | undefined,
							sourceLabel: 'from L1 first line manager',
						}
					: null
				const candidateFallback =
					!candidatePrimary && d?.secondLineManager?.id
						? {
								id: d.secondLineManager.id as string,
								name: d.secondLineManager.name as string,
								photoUrl: d.secondLineManager.photoUrl as string | undefined,
								sourceLabel: 'from L1 second line manager',
							}
						: null
				const candidate = candidatePrimary || candidateFallback
				if (!isValidL2Candidate(candidate)) {
					setL2Suggestion(null)
					return
				}
				setL2Suggestion(candidate)
			} catch {
				setL2Suggestion(null)
			}
		},
		[isValidL2Candidate],
	)

	const onCancel = () => {
		if (embedded) {
			onClose?.()
			return
		}

		navigate('/staff/list')
	}

	const onLookupSearch = async (q: string) => {
		if (!q.trim()) return
		setLookupLoading(true)
		try {
			const results = await staffService.autocomplete(q.trim())
			setLookupResults(results)
		} catch {
			setLookupResults([])
		} finally {
			setLookupLoading(false)
		}
	}

	const onFinish = async (values: any) => {
		try {
			const {
				bankAccount,
				department,
				enName,
				email,
				firstName,
				lastName,
				phoneNumber,
				role,
				gender,
				shift,
				id,
				status,
				extraShift,
				isExtraShift,
				ranking,
			} = formState

			const staffSelfId = (id || '').trim()
			const firstMgrId = (firstLineManager?.id || '').trim()
			const secondMgrId = (secondLineManager?.id || '').trim()
			const thirdMgrId = (thirdLineManager?.id || '').trim()
			if (staffSelfId && firstMgrId && staffSelfId === firstMgrId) {
				message.error('1st line manager cannot be the same as this staff')
				return
			}
			if (staffSelfId && secondMgrId && staffSelfId === secondMgrId) {
				message.error('2nd line manager cannot be the same as this staff')
				return
			}
			if (staffSelfId && thirdMgrId && staffSelfId === thirdMgrId) {
				message.error('3rd line manager cannot be the same as this staff')
				return
			}
			if (firstMgrId && secondMgrId && firstMgrId === secondMgrId) {
				message.error('1st and 2nd line managers must be different people')
				return
			}
			if (firstMgrId && thirdMgrId && firstMgrId === thirdMgrId) {
				message.error('1st and 3rd line managers must be different people')
				return
			}
			if (secondMgrId && thirdMgrId && secondMgrId === thirdMgrId) {
				message.error('2nd and 3rd line managers must be different people')
				return
			}

			const firstLineManagerPayload = buildManagerPayload(
				firstLineManager?.id,
				firstLineManager?.name,
				firstLineManager?.photoUrl,
			)
			const secondLineManagerPayload = buildManagerPayload(
				secondLineManager?.id,
				secondLineManager?.name,
				secondLineManager?.photoUrl,
			)
			const thirdLineManagerPayload = buildManagerPayload(
				thirdLineManager?.id,
				thirdLineManager?.name,
				thirdLineManager?.photoUrl,
			)

			let payloadObj: any = {
				bankAccountNumber: bankAccount,
				departmentId: department,
				email: email,
				firstName: firstName,
				lastName: lastName,
				nameInEnglish: enName,
				id: id,
				phoneNumber: phoneNumber,
				roleId: role,
				sex: gender,
				shiftId: shift,
				// normalize status values (API expects 'InActive')
				status:
					status && String(status).toLowerCase().startsWith('in') ? 'InActive' : status,
				extraShift: extraShift,
				isExtraShift: isExtraShift,
				ranking,
				firstLineManager: firstLineManagerPayload,
				secondLineManager: secondLineManagerPayload,
				thirdLineManager: thirdLineManagerPayload,
			}
			// Only include extraShift when isExtraShift is true
			if (!payloadObj.isExtraShift) {
				delete payloadObj.extraShift
			}
			// Remove empty strings and nil values so legacy empty fields are not sent
			const payload: any = _.omitBy(payloadObj, (v: any) => v === '' || _.isNil(v))

			let createdOrUpdatedStaffId = id

			if (!staffId) {
				const data = await staffService.create(payload)
				if (data?.statusCode !== 200) {
					message.error('Failed to create staff')
					return
				}
				createdOrUpdatedStaffId = data?.data?.id || id
			} else {
				// Debug: log payload and detailed server error response
				console.log('PATCH payload', payload)
				const data = await staffService.update(payload)
				if (data?.statusCode !== 200) {
					message.error('Failed to update staff')
					return
				}
			}

			// Upload photo if selected
			if (photoFile && createdOrUpdatedStaffId) {
				try {
					const formData = new FormData()
					formData.append('photo', photoFile)

					const photoResponse = await staffService.uploadPhoto(
						createdOrUpdatedStaffId,
						formData,
					)
					if (photoResponse?.statusCode === 200) {
						message.success('Photo uploaded successfully')
						console.log('Photo upload response:', photoResponse)
					}
				} catch (photoError: any) {
					console.log('Photo upload error:', photoError?.response?.data || photoError)
					message.warning('Staff saved but photo upload failed')
				}
			}

			setOpenSuccess(true)
		} catch (err: any) {
			console.log('err', err)
			message.error(err?.response?.data?.message || 'An error occurred')
		}
	}

	//   Hook

	const onFetchDepartment = React.useCallback(async () => {
		try {
			departmentService
				.fetchListing()
				.then((data) => {
					if (data?.statusCode === 200) {
						setDepartmentOption(
							(data?.data || []).map((e: any) => ({
								label: e?.name,
								value: e?.id,
							})),
						)
					}
				})
				.catch((err) => console.log('err', err))
		} catch (error) {
			console.log('error', error)
		}
	}, [])

	const onFetchShift = React.useCallback(async () => {
		try {
			shiftService
				.fetchListing()
				.then((data: any) => {
					if (data?.statusCode === 200) {
						let opts = new Array<{ value: string; label: string }>()
						let nightOpts = new Array<{
							value: string
							label: string
						}>()
						;(data?.data || []).forEach((shift: any, i: any) => {
							if (shift.id.startsWith('NIGHT')) {
								nightOpts.push({
									label: shift?.name,
									value: shift?.id,
								})
							} else {
								opts.push({
									label: shift?.name,
									value: shift?.id,
								})
							}
						})

						setShiftOption(opts)
						setNightShiftOption(nightOpts)
					}
				})
				.catch((err: any) => console.log('err', err))
		} catch (error) {
			console.log('error', error)
		}
	}, [])
	const onFetchRole = React.useCallback(async () => {
		try {
			roleService
				.fetchListing()
				.then((data: any) => {
					if (data?.statusCode === 200) {
						setRoleOption(
							(data?.data || []).map((e: any) => ({
								label: e?.name,
								value: e?.id,
							})),
						)
					}
				})
				.catch((err: any) => console.log('err', err))
		} catch (error) {
			console.log('error', error)
		}
	}, [])
	React.useEffect(() => {
		if (!!staffId) {
			staffService
				.detail(staffId)
				.then((res) => {
					const data = {
						firstName: res?.data?.firstName,
						lastName: res?.data?.lastName,
						enName: res?.data?.nameInEnglish,
						gender: res?.data?.sex,
						department: res?.data?.departmentId || res?.data?.department?.id,
						shift: res?.data?.shiftId || res?.data?.shift?.id,
						role: res?.data?.roleId || res?.data?.role?.id,
						bankAccount: res?.data?.bankAccountNumber,
						phoneNumber: res?.data?.phoneNumber,
						email: res?.data?.email,
						id: res?.data?.id,
						status:
							res?.data?.status &&
							String(res?.data?.status).toLowerCase().startsWith('in')
								? 'InActive'
								: res?.data?.status,
						extraShift: res?.data?.extraShift,
						isExtraShift: res?.data?.isExtraShift,
						photoUrl: res?.data?.photoUrl,
						ranking: res?.data?.ranking,
					}
					setFormState(data)
					form.setFieldsValue(data)
					// Populate manager lookup state
					setFirstLineManager(
						res?.data?.firstLineManager?.id
							? {
									id: res.data.firstLineManager.id,
									name: res.data.firstLineManager.name || '',
									photoUrl: res.data.firstLineManager.photoUrl,
								}
							: null,
					)
					setSecondLineManager(
						res?.data?.secondLineManager?.id
							? {
									id: res.data.secondLineManager.id,
									name: res.data.secondLineManager.name || '',
									photoUrl: res.data.secondLineManager.photoUrl,
								}
							: null,
					)
					setThirdLineManager(
						res?.data?.thirdLineManager?.id
							? {
									id: res.data.thirdLineManager.id,
									name: res.data.thirdLineManager.name || '',
									photoUrl: res.data.thirdLineManager.photoUrl,
								}
							: null,
					)
					setL2SuggestionDismissed(false)
					refreshL2Suggestion(res?.data?.firstLineManager?.id || null)
					// Migration: add missing timestamp fields for legacy records so backend won't fail
					const missingDates: any = {}
					if (!res?.data?.createdAt) missingDates.createdAt = new Date().toISOString()
					if (!res?.data?.updatedAt) missingDates.updatedAt = new Date().toISOString()
					if (Object.keys(missingDates).length > 0) {
						console.log(
							'Migrating staff record to add missing date fields',
							staffId,
							missingDates,
						)
						staffService
							.update({ id: staffId, ...missingDates })
							.then((r) => console.log('Migration result', r))
							.catch((e: any) =>
								console.log('Migration error', e?.response?.data || e),
							)
					}
				})
				.catch((err) => console.log('err', err))
		}
	}, [staffId, refreshL2Suggestion])
	React.useEffect(() => {
		onFetchDepartment()
		onFetchShift()
		onFetchRole()
	}, [])

	return (
		<>
			{!embedded && <GlobalHeader />}
			{!embedded && (
				<div style={{ width: '100%' }}>
					<Card
						color='primary'
						style={{ margin: '24px 16px' }}
						styles={{
							body: {
								fontSize: 18,
								backgroundColor: Colors.White,
								textAlign: 'center',
								borderRadius: 6,
								color: Colors.Primary,
							},
						}}
					>
						<Typography.Title
							color='secondary'
							aria-level={2}
							style={{
								color: Colors.Primary,
								fontFamily: 'Times New Roman',
							}}
						>
							Staff Information
						</Typography.Title>
					</Card>
				</div>
			)}
			<Form
				layout='vertical'
				autoComplete='off'
				form={form}
				onFinish={onFinish}
				size='large'
				style={{ fontSize: 16 }}
			>
				<Card style={{ margin: '0px 16px' }} styles={{ body: { padding: 28 } }}>
					<Typography.Title level={2} style={{ marginLeft: '24px' }}>
						{!staffId ? 'Create Staff Information' : 'Edit Staff Information'}
					</Typography.Title>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item name='id' rules={[{ required: true }]} label='ID'>
								<Input
									name='id'
									value={formState.id}
									onChange={handleChange}
									placeholder='Enter staff ID'
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item name='status' rules={[{ required: true }]} label='Status'>
								<Select
									aria-required
									showSearch
									placeholder='Select  status'
									optionFilterProp='children'
									value={formState.status}
									onChange={(e) =>
										handleChange({
											target: {
												name: 'status',
												value: e,
											},
										})
									}
									filterOption={(input, option) =>
										(option?.label ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={statusOption}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item name='ranking' label='Ranking'>
								<Select
									allowClear
									showSearch
									placeholder='Select ranking'
									optionFilterProp='children'
									onChange={(e) =>
										handleChange({
											target: {
												name: 'ranking',
												value: e,
											},
										})
									}
									filterOption={(input, option) =>
										(option?.label ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={rankingOption}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item label='Staff Photo'>
								<Space direction='vertical' style={{ width: '100%' }}>
									{formState.photoUrl && (
										<Image
											src={formState.photoUrl}
											alt='Staff photo'
											style={{
												width: 120,
												height: 120,
												objectFit: 'cover',
												borderRadius: 8,
											}}
										/>
									)}
									<Upload
										showUploadList={false}
										beforeUpload={() => false}
										onChange={handlePhotoUpload}
										accept='image/jpeg,image/png,image/webp'
									>
										<Button icon={<UploadOutlined />}>
											{formState.photoUrl ? 'Change Photo' : 'Upload Photo'}
										</Button>
									</Upload>
									<Typography.Text type='secondary' style={{ fontSize: 12 }}>
										JPEG, PNG, or WebP (max 5MB)
									</Typography.Text>
								</Space>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item
								name='firstName'
								rules={[{ required: true }]}
								label='First Name'
							>
								<Input
									name='firstName'
									value={formState.firstName}
									onChange={handleChange}
									placeholder='Enter first Name'
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name='lastName'
								rules={[{ required: true }]}
								label='Last Name'
							>
								<Input
									name='lastName'
									defaultValue={formState.lastName}
									value={formState.lastName}
									onChange={handleChange}
									placeholder='Enter last Name'
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item
								name='enName'
								rules={[{ required: true }]}
								label='Name in English'
							>
								<Input
									name='enName'
									value={formState.enName}
									onChange={handleChange}
									placeholder='Enter name in English'
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item name='gender' rules={[{ required: true }]} label='Gender'>
								<Select
									aria-required
									showSearch
									placeholder='Select  gender'
									optionFilterProp='children'
									value={formState.gender}
									onChange={(e) =>
										handleChange({
											target: {
												name: 'gender',
												value: e,
											},
										})
									}
									filterOption={(input, option) =>
										(option?.label ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={genderOption}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item
								name='department'
								rules={[{ required: true }]}
								label='Department'
							>
								<Select
									aria-required
									showSearch
									placeholder='Select department'
									optionFilterProp='children'
									onChange={(e) =>
										handleChange({
											target: {
												name: 'department',
												value: e,
											},
										})
									}
									filterOption={(input, option) =>
										(option?.label ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={departmentOption}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item name='shift' rules={[{ required: true }]} label='Shift'>
								<Select
									aria-required
									showSearch
									placeholder='Select shift'
									optionFilterProp='children'
									onChange={(e) =>
										handleChange({
											target: { name: 'shift', value: e },
										})
									}
									filterOption={(input, option) =>
										(option?.label ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={shiftOption}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item name='role' rules={[{ required: true }]} label='Role'>
								<Select
									aria-required
									showSearch
									placeholder='Select role'
									optionFilterProp='children'
									onChange={(e) =>
										handleChange({
											target: { name: 'role', value: e },
										})
									}
									filterOption={(input, option) =>
										(option?.label ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={roleOption}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name='bankAccount'
								rules={[{ required: true }]}
								label='Bank Account'
							>
								<Input
									name='bankAccount'
									value={formState.bankAccount}
									onChange={handleChange}
									placeholder='Enter bank account'
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item name='phoneNumber' label='Phone Number'>
								<Input
									name='phoneNumber'
									value={formState.phoneNumber}
									onChange={handleChange}
									placeholder='Enter phone number'
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item name='email' label='Email'>
								<Input
									name='email'
									value={formState.email}
									onChange={handleChange}
									placeholder='Enter email'
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item label='1st Line Manager'>
								<Space wrap>
									{firstLineManager ? (
										<>
											<Avatar
												src={firstLineManager.photoUrl}
												icon={<UserOutlined />}
												style={{ flexShrink: 0 }}
											/>
											<Typography.Text strong>
												{firstLineManager.name}
											</Typography.Text>
											<Typography.Text
												type='secondary'
												style={{ fontSize: 12 }}
											>
												{firstLineManager.id}
											</Typography.Text>
											<Button
												size='small'
												danger
												icon={<CloseCircleOutlined />}
												onClick={() => {
													setFirstLineManager(null)
													setL2Suggestion(null)
													setL2SuggestionDismissed(false)
												}}
											>
												Clear
											</Button>
										</>
									) : (
										<Typography.Text type='secondary'>
											No manager selected
										</Typography.Text>
									)}
									<Button
										icon={<SearchOutlined />}
										onClick={() => {
											setLookupSearch('')
											setLookupResults([])
											setLookupTarget('first')
										}}
									>
										{firstLineManager ? 'Change' : 'Select'}
									</Button>
								</Space>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label='2nd Line Manager'>
								<Space wrap>
									{secondLineManager ? (
										<>
											<Avatar
												src={secondLineManager.photoUrl}
												icon={<UserOutlined />}
												style={{ flexShrink: 0 }}
											/>
											<Typography.Text strong>
												{secondLineManager.name}
											</Typography.Text>
											<Typography.Text
												type='secondary'
												style={{ fontSize: 12 }}
											>
												{secondLineManager.id}
											</Typography.Text>
											<Button
												size='small'
												danger
												icon={<CloseCircleOutlined />}
												onClick={() => {
													setSecondLineManager(null)
													setL2SuggestionDismissed(false)
												}}
											>
												Clear
											</Button>
										</>
									) : (
										<Typography.Text type='secondary'>
											No manager selected
										</Typography.Text>
									)}
									<Button
										icon={<SearchOutlined />}
										onClick={() => {
											setLookupSearch('')
											setLookupResults([])
											setLookupTarget('second')
										}}
									>
										{secondLineManager ? 'Change' : 'Select'}
									</Button>
								</Space>
								{l2Suggestion &&
								!l2SuggestionDismissed &&
								(!secondLineManager || secondLineManager.id !== l2Suggestion.id) ? (
									<div style={{ marginTop: 8 }}>
										<Typography.Text type='secondary' style={{ fontSize: 12 }}>
											Suggested from L1 hierarchy:{' '}
											<strong>
												{l2Suggestion.name} ({l2Suggestion.id})
											</strong>
										</Typography.Text>
										<Space size='small' style={{ marginLeft: 8 }}>
											<Button
												size='small'
												type='link'
												onClick={() =>
													setSecondLineManager({
														id: l2Suggestion.id,
														name: l2Suggestion.name,
														photoUrl: l2Suggestion.photoUrl,
													})
												}
											>
												Apply
											</Button>
											<Button
												size='small'
												type='link'
												onClick={() => setL2SuggestionDismissed(true)}
											>
												Dismiss
											</Button>
										</Space>
									</div>
								) : null}
								{!!firstLineManager &&
								!secondLineManager &&
								!l2SuggestionDismissed &&
								!l2Suggestion ? (
									<div style={{ marginTop: 8 }}>
										<Typography.Text type='secondary' style={{ fontSize: 12 }}>
											No second-line suggestion found from the selected first-line
											manager.
										</Typography.Text>
									</div>
								) : null}
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item label='3rd Line Manager'>
								<Space wrap>
									{thirdLineManager ? (
										<>
											<Avatar
												src={thirdLineManager.photoUrl}
												icon={<UserOutlined />}
												style={{ flexShrink: 0 }}
											/>
											<Typography.Text strong>
												{thirdLineManager.name}
											</Typography.Text>
											<Typography.Text
												type='secondary'
												style={{ fontSize: 12 }}
											>
												{thirdLineManager.id}
											</Typography.Text>
											<Button
												size='small'
												danger
												icon={<CloseCircleOutlined />}
												onClick={() => setThirdLineManager(null)}
											>
												Clear
											</Button>
										</>
									) : (
										<Typography.Text type='secondary'>
											No manager selected
										</Typography.Text>
									)}
									<Button
										icon={<SearchOutlined />}
										onClick={() => {
											setLookupSearch('')
											setLookupResults([])
											setLookupTarget('third')
										}}
									>
										{thirdLineManager ? 'Change' : 'Select'}
									</Button>
								</Space>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[24, 10]}>
						<Col xs={24} md={12}>
							<Form.Item name='isExtraShift' label='Night Shift Emergency ?'>
								<Switch
									checked={formState.isExtraShift}
									onChange={handleChangeSwitch}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name='extraShift'
								// rules={[{ required: true }]}
								label='Night Shift Team'
							>
								<Select
									disabled={!formState.isExtraShift}
									aria-required
									showSearch
									placeholder='Select night shift'
									optionFilterProp='children'
									onChange={(e) =>
										handleChange({
											target: {
												name: 'extraShift',
												value: e,
											},
										})
									}
									filterOption={(input, option) =>
										(option?.label ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={nightShiftOption}
								/>
							</Form.Item>
						</Col>
					</Row>
					<div style={{ textAlign: 'right' }}>
						<Space>
							<Button htmlType='button' onClick={onCancel}>
								Cancel
							</Button>
							<Button type='primary' htmlType='submit'>
								{!staffId ? 'Create' : 'Update'}
							</Button>
						</Space>
					</div>
				</Card>
				{/* <Footer
					style={{
						textAlign: 'end',
						bottom: 0,
						background: Colors.White,
						position: 'fixed',
						width: '100%',
						borderRadius: 8,
						padding: 8,
						marginTop: 16,
					}}
				>
					<Button type='primary' htmlType='submit'>
						{!staffId ? 'Create' : 'Update'}
					</Button>
				</Footer> */}
			</Form>
			<Modal
				open={lookupTarget !== null}
				title={
					lookupTarget === 'first'
						? 'Select 1st Line Manager'
						: lookupTarget === 'second'
							? 'Select 2nd Line Manager'
							: 'Select 3rd Line Manager'
				}
				onCancel={() => {
					setLookupTarget(null)
					setLookupSearch('')
					setLookupResults([])
				}}
				footer={null}
				width={480}
			>
				<Input.Search
					placeholder='Search by name…'
					loading={lookupLoading}
					value={lookupSearch}
					onChange={(e) => setLookupSearch(e.target.value)}
					onSearch={onLookupSearch}
					enterButton
					style={{ marginBottom: 12 }}
				/>
				<List
					dataSource={lookupResults}
					locale={{
						emptyText: lookupSearch ? 'No results found' : 'Type a name to search',
					}}
					renderItem={(item) => (
						<List.Item
							style={{ cursor: 'pointer' }}
							onClick={() => {
								const ref = {
									id: item.id,
									name: item.name,
									photoUrl: item.photoUrl,
								}
								if (lookupTarget === 'first') {
									setFirstLineManager(ref)
									setL2SuggestionDismissed(false)
									refreshL2Suggestion(ref.id)
								} else if (lookupTarget === 'second') {
									setSecondLineManager(ref)
								} else {
									setThirdLineManager(ref)
								}
								setLookupTarget(null)
								setLookupSearch('')
								setLookupResults([])
							}}
						>
							<List.Item.Meta
								avatar={<Avatar src={item.photoUrl} icon={<UserOutlined />} />}
								title={item.name}
								description={item.nameKh ? `${item.nameKh} · ${item.id}` : item.id}
							/>
						</List.Item>
					)}
				/>
			</Modal>
			<SuccessfulModal
				title={staffId ? 'Updated successfully' : 'Created successfully'}
				desc={staffId ? 'Staff is updated successfully.' : 'Staff is created successfully.'}
				open={openSuccess}
				onOk={() => {
					if (embedded) {
						onSuccess?.()
						onClose?.()
						setOpenSuccess(false)
						return
					}

					navigate('/staff/list')
					setOpenSuccess(false)
				}}
			/>
		</>
	)
}

export default StaffCreateUpdate
